import { noop } from '@kofno/piper';
import { fromNullable, just, Maybe, nothing } from 'maybeasy';
import { action, computed, makeAutoObservable, observable, runInAction } from 'mobx';
import { previewStore } from '../../../LearningCollections/LearningCollection/ChatbotPreview/LearningCollectionPreviewStore';
import { AssistantRole } from '../../Types';
import {
  ChatContext,
  ChatMetadataResource,
  ChatMetadataState,
  fetchChatMetadataResource,
  loading,
  ProductLicense,
  putProductLicense,
  ready,
  waiting,
} from './Types';
import { equals } from '@execonline-inc/collections';

export class ChatMetadataStore {
  @observable state: ChatMetadataState = waiting();

  constructor() {
    makeAutoObservable(this);
  }

  @action
  loading(chatContext: ChatContext): void {
    this.state = loading(chatContext);
    this.fetchChatMetadata();
  }

  @action
  updateSelectedProductLicense = (productLicenseId: number): void => {
    const state = this.state;

    switch (state.kind) {
      case 'waiting':
      case 'loading':
        break;
      case 'ready':
        putProductLicense(productLicenseId).fork(noop, (resource) => {
          runInAction(() => {
            state.chatContext.licenseId = fromNullable<number>(resource.payload.productLicenseId);

            resource.payload.stillAvailableRecommendationIds.map((ids) =>
              previewStore.filterStillAvailableRecommendations(ids),
            );
          });
        });
        break;
    }
  };

  @computed
  get assistantRole(): AssistantRole {
    const state = this.state;

    switch (state.kind) {
      case 'waiting':
      case 'loading':
        return 'default';
      case 'ready':
        return state.chatContext.assistantRole;
    }
  }

  @computed
  get courseRegistrationId(): Maybe<number> {
    const state = this.state;

    switch (state.kind) {
      case 'waiting':
      case 'loading':
        return nothing();
      case 'ready':
        return state.chatContext.courseRegistrationId;
    }
  }

  private maybeProductLicense = ({
    chatMetadataResource,
    chatContextLicenseId,
  }: {
    chatMetadataResource: ChatMetadataResource;
    chatContextLicenseId: number;
  }): Maybe<ProductLicense> =>
    fromNullable(
      chatMetadataResource.payload.productLicenses.find((license) =>
        equals(license.id, chatContextLicenseId),
      ),
    );

  @computed get selectedProductLicense(): Maybe<ProductLicense> {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'loading':
        return nothing();
      case 'ready':
        return just({})
          .assign('chatMetadataResource', state.chatMetadataResource)
          .assign('chatContextLicenseId', state.chatContext.licenseId)
          .andThen(this.maybeProductLicense);
    }
  }

  private fetchChatMetadata = (): void => {
    const state = this.state;

    switch (state.kind) {
      case 'waiting':
        break;
      case 'loading':
        state.chatContext.assistantRole === 'default'
          ? fetchChatMetadataResource().fork(noop, (resource) =>
              runInAction(() => {
                this.state = ready(just(resource), state);
              }),
            )
          : (this.state = ready(nothing(), state));
        break;
      case 'ready':
        break;
    }
  };
}

const chatMetadataStore = new ChatMetadataStore();

export { chatMetadataStore };
